/**
 * Self-heal on stale chunk-load errors after a deploy.
 *
 * When a user has a cached index.html pointing at chunk filenames that no
 * longer exist on the server, dynamic imports 404 and React raises
 * ChunkLoadError. This module detects that case and reloads the page once
 * (gated by sessionStorage so a real persistent error still surfaces).
 */

const RELOAD_FLAG = 'promedic-chunk-reload';

const CHUNK_ERROR_PATTERN = /Loading (chunk|CSS chunk) \d+ failed|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

export const isChunkLoadError = (error) => {
  if (!error) return false;
  const name = error.name || '';
  if (name === 'ChunkLoadError') return true;
  let msg = '';
  try {
    msg = (error.message || error.toString() || '').toString();
  } catch (_) {
    msg = '';
  }
  return CHUNK_ERROR_PATTERN.test(msg);
};

export const reloadOnceForChunk = () => {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG) === '1') return;
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch (_) {
    // Private mode / quota: still attempt the reload, accept loop risk
  }
  window.location.reload();
};

export const installChunkErrorHandler = () => {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event && (event.error || event))) {
      reloadOnceForChunk();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event && event.reason)) {
      reloadOnceForChunk();
    }
  });

  // Clear the flag once we know the load really succeeded
  const clearFlag = () => {
    setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch (_) {
        /* ignore */
      }
    }, 5000);
  };

  if (document.readyState === 'complete') {
    clearFlag();
  } else {
    window.addEventListener('load', clearFlag);
  }
};
